import { Controller } from "@hotwired/stimulus"
import { captureMessage, withScope, getClient } from "@sentry/browser"

// Loading / error states for question images in the test practice flow.
//
// Module scope, not instance scope: the practice form lives in a turbo frame
// that is replaced on every answer, so this controller connects and disconnects
// dozens of times per session. Dedupe state has to outlive the instances.
//
// Sentry is at quota (docs/datadog_production_issues_2026-07-08.md: 8,005 HTTP
// 429s in 14 days, >85% of events dropped in one window), so every failure is
// deduped per blob+reason and the page is hard-capped.
const MAX_REPORTS = 10
const reportedKeys = new Set()
let reportCount = 0

// An <img> that fails tells us nothing about *why*: no status, no error body.
// These three answer the questions the bare event cannot. `workerStart > 0` says
// the request went through the service worker; `responseStatus` is the status the
// browser saw (0 when a cross-origin redirect strips it without Timing-Allow-Origin);
// and re-fetching the same URL separates "the bytes are gone" (403/404) from
// "something ate the request" (TypeError).
const TIMING_FIELDS = ["workerStart", "responseStatus", "transferSize", "encodedBodySize", "nextHopProtocol"]

const timingFor = (src) => {
  try {
    const entry = performance.getEntriesByName(src).pop()
    if (!entry) return null

    const timing = Object.fromEntries(TIMING_FIELDS.map((f) => [f, entry[f]]))
    timing.duration = Math.round(entry.duration)
    return timing
  } catch {
    return null
  }
}

const PROBE_TIMEOUT = 5000

// A hung probe must never swallow the report it was meant to explain: the same
// bad connection that fails the image can leave fetch() pending forever, and the
// dedupe slot is already spent by then, so the failure would vanish silently and
// the absence of events would look like a fix.
const withTimeout = (promise, fallback) =>
  Promise.race([
    promise,
    new Promise((resolve) => setTimeout(() => resolve(fallback), PROBE_TIMEOUT))
  ])

const refetch = (src) =>
  withTimeout(
    fetch(src, { cache: "reload" })
      .then((r) => ({ status: r.status, ok: r.ok, type: r.type, redirected: r.redirected }))
      .catch((e) => ({ error: `${e.name}: ${String(e.message).slice(0, 80)}` })),
    { error: "probe timed out" }
  )

const diagnose = (src) =>
  refetch(src)
    .then((result) => ({
      serviceWorkerControlled: !!(navigator.serviceWorker && navigator.serviceWorker.controller),
      timing: timingFor(src),
      refetch: result
    }))
    .catch((e) => ({ diagnosticsError: String(e) }))

const reportFailure = (key, reason, context, src) => {
  // Offline drops are the dominant failure mode and are not actionable —
  // sentry.js already filters their network-error equivalents.
  if (navigator.onLine === false) return
  // init() is host-gated in sentry.js; without a client this is a no-op.
  if (!getClient()) return
  if (reportCount >= MAX_REPORTS) return
  if (reportedKeys.has(key)) return

  // Claim the slot before the async diagnostics so a burst of failing images
  // cannot race past the cap while the probes are in flight.
  reportedKeys.add(key)
  reportCount += 1

  diagnose(src).then((diagnostics) => {
    try {
      withScope((scope) => {
        scope.setTag("feature", "question-image")
        scope.setTag("image_failure_reason", reason)
        // Tags, not just context, so these are aggregatable in Sentry search.
        scope.setTag("image_sw_controlled", String(diagnostics.serviceWorkerControlled))
        const probe = diagnostics.refetch || {}
        scope.setTag("image_refetch_status", String(probe.status ?? probe.error ?? "unknown"))
        scope.setContext("question_image", context)
        scope.setContext("question_image_diagnostics", diagnostics)
        captureMessage(`Question image failed to load (${reason})`, "warning")
      })
    } catch {
      // Telemetry must never break the practice UI.
    }
  })
}

export default class extends Controller {
  static targets = ["img", "placeholder", "error"]
  static values = {
    timeout: { type: Number, default: 15000 }
  }

  connect() {
    this.settled = false
    this.retries = 0
    // Pristine URL: retries rebuild from this so cache-busting params never stack.
    this.originalSrc = this.imgTarget.src

    this._onLoad = this.handleLoad.bind(this)
    this._onError = this.handleError.bind(this)
    this._onOnline = this.handleBackOnline.bind(this)
    this._onMorph = this.handleMorph.bind(this)

    // load/error do not bubble, so these bind to the <img> itself.
    this.imgTarget.addEventListener("load", this._onLoad)
    this.imgTarget.addEventListener("error", this._onError)
    document.addEventListener("turbo:morph", this._onMorph)

    // The next question's image is preloaded while the learner answers the
    // current one, so by the time we connect it has usually already resolved
    // and its load event has fired.
    if (this.imgTarget.complete && this.imgTarget.naturalWidth > 0) {
      this.handleLoad()
      return
    }

    this.showLoading()

    // A lazy image below the fold never starts its fetch, so the stall timeout
    // would paint the error state on rows the learner simply hasn't scrolled
    // to. Arm it only once the image nears the viewport; the browser's own
    // lazy threshold is wider than this margin, so the timeout can start late
    // but never falsely early.
    if (this.imgTarget.loading === "lazy" && !this.imgTarget.complete) {
      this.armTimeoutNearViewport()
    } else {
      this.startTimeout()
    }

    // complete + naturalWidth === 0 is NOT proof of failure. WebKit flips
    // complete to true as soon as the bytes arrive, while naturalWidth stays 0
    // until the decode finishes — and decoding="async" defers that decode on
    // purpose. A preloaded image (i.e. every question image but the first)
    // therefore lands in that window routinely, and reading it as a failure
    // painted the error state over images that were about to appear; the real
    // load event that followed was then swallowed by the settled guard.
    // A genuinely broken image has already fired error and will not fire it
    // again, so decode() arbitrates: it resolves once the image is usable and
    // rejects when it truly cannot be decoded.
    if (!this.imgTarget.complete || !this.originalSrc) return

    if (typeof this.imgTarget.decode === "function") this.settleWithDecode()
    else this.handleError()
  }

  // decode() settles asynchronously, by which point the element may have moved
  // on to another image (retry, or a frame swap that reused the node). Ignore a
  // verdict that no longer describes the image we asked about.
  settleWithDecode() {
    const src = this.imgTarget.src
    const stale = () => this.settled || !this.hasImgTarget || this.imgTarget.src !== src

    this.imgTarget.decode().then(
      () => { if (!stale()) this.handleLoad() },
      () => { if (!stale()) this.handleError() }
    )
  }

  armTimeoutNearViewport() {
    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return
      this.clearObserver()
      if (!this.settled) this.startTimeout()
    }, { rootMargin: "300px" })
    this.observer.observe(this.imgTarget)
  }

  clearObserver() {
    if (!this.observer) return
    this.observer.disconnect()
    this.observer = null
  }

  disconnect() {
    this.clearTimeout()
    this.clearObserver()
    window.removeEventListener("online", this._onOnline)
    document.removeEventListener("turbo:morph", this._onMorph)
    // Stimulus can no longer resolve targets once the element leaves this
    // controller's scope, and a throw here would strand the teardown.
    if (!this.hasImgTarget) return
    this.imgTarget.removeEventListener("load", this._onLoad)
    this.imgTarget.removeEventListener("error", this._onError)
  }

  retry() {
    this.retries += 1
    this.settled = false
    this.showLoading()
    this.startTimeout()
    // Bypass the browser's cached failure. The signed_id sits in the path of the
    // ActiveStorage redirect URL, so an extra query param leaves it verifiable.
    const separator = this.originalSrc.includes("?") ? "&" : "?"
    this.imgTarget.src = `${this.originalSrc}${separator}_retry=${Date.now()}`
  }

  handleLoad() {
    if (this.settled) return
    this.settled = true
    this.clearTimeout()
    this.clearObserver()
    window.removeEventListener("online", this._onOnline)
    this.showImage()
  }

  handleError() {
    if (this.settled) return
    this.settle("error")
  }

  handleTimeout() {
    if (this.settled) return
    this.settle("timeout")
  }

  handleBackOnline() {
    if (this.imgTarget.naturalWidth > 0) return
    this.retry()
  }

  // The layouts refresh with morph (e.g. the theme toggle submits and
  // redirect_back morphs this same page). A morph rewrites the class
  // attributes back to the server markup — skeleton pulsing, img transparent —
  // without reconnecting this controller, so re-apply the settled state.
  handleMorph() {
    if (!this.hasImgTarget || !this.settled) return
    this.imgTarget.naturalWidth > 0 ? this.showImage() : this.showError()
  }

  settle(reason) {
    this.settled = true
    this.clearTimeout()
    this.clearObserver()
    this.showError()
    reportFailure(this.reportKey(reason), reason, this.reportContext(), this.originalSrc)
    // Recover by itself once the connection is back rather than making the
    // learner notice and tap Retry.
    if (navigator.onLine === false) {
      window.addEventListener("online", this._onOnline, { once: true })
    }
  }

  // View state. `hidden` and `flex` are applied as mutually exclusive classes
  // rather than relying on their order in the compiled stylesheet. Each of these
  // sets the full state, so they stay correct on a retry or a reused element.

  showLoading() {
    this.errorTarget.classList.add("hidden")
    this.errorTarget.classList.remove("flex")
    this.imgTarget.classList.remove("hidden")
    this.imgTarget.classList.add("opacity-0", "pointer-events-none")
    this.placeholderTarget.classList.remove("hidden")
  }

  showImage() {
    this.placeholderTarget.classList.add("hidden")
    this.errorTarget.classList.add("hidden")
    this.errorTarget.classList.remove("flex")
    this.imgTarget.classList.remove("hidden", "opacity-0", "pointer-events-none")
  }

  showError() {
    // Hiding the img keeps image-zoom from opening an overlay on a broken image.
    this.imgTarget.classList.add("hidden")
    this.placeholderTarget.classList.add("hidden")
    this.errorTarget.classList.remove("hidden")
    this.errorTarget.classList.add("flex")
  }

  startTimeout() {
    this.clearTimeout()
    // A stalled storage response fires neither load nor error.
    this.timeoutId = window.setTimeout(() => this.handleTimeout(), this.timeoutValue)
  }

  clearTimeout() {
    if (!this.timeoutId) return
    window.clearTimeout(this.timeoutId)
    this.timeoutId = null
  }

  reportKey(reason) {
    // The pathname carries the blob's signed_id, so retries of the same broken
    // image collapse into a single event.
    let path = this.originalSrc
    try {
      path = new URL(this.originalSrc, window.location.origin).pathname
    } catch {
      // Keep the raw src as the key.
    }
    return `${path}:${reason}`
  }

  reportContext() {
    return {
      src: this.originalSrc,
      retries: this.retries,
      online: navigator.onLine,
      // complete distinguishes "the browser gave up on this image" from "we
      // gave up on the browser" (timeout), which naturalWidth alone cannot.
      complete: this.imgTarget.complete,
      naturalWidth: this.imgTarget.naturalWidth,
      viewport: `${window.innerWidth}x${window.innerHeight}`
    }
  }
}
